import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getConfig } from '../../config/appConfig';
import apiService from '../../services/apiService';
import { apiUrl } from '../../utils/apiBase';
import {
  getAuthApiKey,
  getAuthUser,
  setAuthSession,
  setAuthUserProfile,
  setCsrfToken,
  candorWebRequestAuth,
  hasCookieSession,
} from '../../utils/authTokenStore';
import { clearAllAuthStorage } from '../../utils/sessionManager';
import { exchangeIdeHandoffTicket, readHandoffAuthCode } from '../../utils/ideHandoff';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  Paper,
  Fade,
  useTheme,
  useMediaQuery,
  Stack,
  Button,
  Tooltip,
  Switch,
  FormControlLabel,
  LinearProgress,
  Backdrop,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CreditCard as CreditCardIcon,
  Settings as SettingsIcon,
  AddCircle as AddCircleIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Menu as MenuIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { useThemeMode } from '../../context/ThemeModeContext';
import DashboardOverview from './DashboardOverview';
import PageHeader from '../../components/layout/PageHeader';
import { SECTION_META } from './sectionMeta';
import SubscriptionPlans from '../plans/SubscriptionPlans';
import AddOnPlans from '../plans/AddOnPlans';
import BillingHistory from '../billing/BillingHistory';
import LoadingScreen from '../../components/layout/LoadingScreen';
import UserAccountMenu from '../../components/layout/UserAccountMenu';
import AppHeaderBrand from '../../components/layout/AppHeaderBrand';
import AppToolbarActions from '../../components/layout/AppToolbarActions';
import ExpiredSubscriptionModal from '../../components/common/ExpiredSubscriptionModal';
import CompliancePolicyModal from '../../components/common/CompliancePolicyModal';
// Settings modal not needed; using simple placeholder view
// Removed deleted components - using simplified dashboard

const Dashboard = () => {
  const tokenStorageKey = getConfig('security.tokenStorageKey') || 'dashboard_token';
  const { user: authUser, token: authToken, loading: authContextLoading } = useAuth();

  // Real user authentication from CDP IDE
  const [user, setUser] = useState(null);
  // Simplified settings: no modal, just static placeholder
  const [authLoading, setAuthLoading] = useState(true);
  const { subscription, loading: subscriptionLoading, refreshData } = useSubscription();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [logoutProgress, setLogoutProgress] = useState(0);
  const [activeComplianceModal, setActiveComplianceModal] = useState(null);
  const hasShownExpiredModalRef = useRef(false);

  const persistSecureSession = useCallback((sessionPayload) => {
    if (!sessionPayload || typeof window === 'undefined') {
      return null;
    }

    const payloadUser = sessionPayload.user ||
      sessionPayload.profile ||
      sessionPayload.session?.user ||
      null;

    const sanitizedUser = payloadUser ? {
      email: payloadUser.email,
      name: payloadUser.name,
      company: payloadUser.company,
      role: payloadUser.role,
      plan_name: payloadUser.plan_name,
      status: payloadUser.status,
    } : null;

    const sessionToken = sessionPayload.session_token ||
      sessionPayload.api_key ||
      sessionPayload.token ||
      sessionPayload.session?.token ||
      null;

    const csrfToken = sessionPayload.csrf_token ||
      sessionPayload.session?.csrf_token ||
      null;

    try {
      if (sessionToken) {
        setAuthSession(sessionToken, sanitizedUser || getAuthUser());
      } else if (sanitizedUser) {
        setAuthUserProfile(sanitizedUser);
      }

      if (csrfToken) {
        setCsrfToken(csrfToken);
      }

      if (sessionPayload.expires_at || sessionPayload.session?.expires_at) {
        const expiry = sessionPayload.expires_at || sessionPayload.session?.expires_at;
        localStorage.setItem('candor_session_expires_at', String(expiry));
      }
    } catch (storageError) {
      // Swallow storage errors (private browsing, etc.)
    }

    return sanitizedUser;
  }, [tokenStorageKey]);

  const establishSecureSession = useCallback(async (authCode) => {
    if (!authCode) {
      return false;
    }

    const result = await exchangeIdeHandoffTicket(authCode, {
      source: 'subscription_dashboard',
    });
    if (!result.ok) {
      return false;
    }

    const sessionPayload = {
      user: result.user,
      session_token: result.sessionToken,
      csrf_token: undefined,
      session_channel: result.cookieSession ? 'cookie' : undefined,
      token_type: result.cookieSession ? 'cookie' : undefined,
    };

    const sanitizedUser = persistSecureSession(sessionPayload);
    if (sanitizedUser) {
      setUser(sanitizedUser);
    } else if (result.user) {
      setUser(result.user);
    }

    return true;
  }, [persistSecureSession]);

  // Authenticate user from CDP IDE
  useEffect(() => {
    if (authContextLoading) {
      return;
    }

    const authenticateUser = async () => {
      try {
        setAuthLoading(true);

        // AuthContext already exchanged the handoff ticket — avoid duplicate profile API call
        if (authToken && authUser?.email) {
          const sessionUser = {
            email: authUser.email,
            name: authUser.name,
            company: authUser.company,
            role: authUser.role,
          };
          setUser(sessionUser);
          setAuthUserProfile(sessionUser);
          setAuthLoading(false);
          return;
        }

        // Clear any cached mock user data
        const cachedCdpUserObj = getAuthUser();
        const cachedCdpUser = cachedCdpUserObj ? JSON.stringify(cachedCdpUserObj) : null;
        if (cachedCdpUser) {
          const userData = JSON.parse(cachedCdpUser);
          if (userData.email === 'test@candorsys.com' || userData.email === 'demo@example.com') {
            clearAllAuthStorage();
            localStorage.removeItem('vscode_user');
            localStorage.removeItem('candor_vscode_user');
          }
        }

        // Method 0: Attempt secure session exchange using short-lived auth code
        const authCode = readHandoffAuthCode();
        if (authCode) {
          try {
            const established = await establishSecureSession(authCode);
            if (established) {
              setAuthLoading(false);
              return;
            }
          } catch (sessionError) {
            // If secure exchange fails, fall through to legacy flows
          }
        }

        // Method 1: Check for user data from URL parameters (VS Code extension)
        const urlParams = new URLSearchParams(window.location.search);
        const userParam = urlParams.get('user');
        if (userParam) {
          try {
            const userData = JSON.parse(decodeURIComponent(userParam));
            const { api_key: _discardKey, ...safeUser } = userData || {};
            setUser(safeUser);
            if (userData.api_key) {
              setAuthSession(userData.api_key, safeUser);
            }

            if (typeof window !== 'undefined') {
              window.history.replaceState({}, document.title, window.location.pathname);
            }

            setAuthLoading(false);
            return;
          } catch (error) {
            // Ignore malformed data to avoid leaking info
          }
        }

        // Method 1.5: Check for user data from CDP IDE via localStorage
        const cdpUserObj = getAuthUser();
        const cdpUser = cdpUserObj ? JSON.stringify(cdpUserObj) : null;
        if (cdpUser) {
          const userData = JSON.parse(cdpUser);
          const { api_key: _discardStored, ...safeStored } = userData || {};
          setUser(safeStored);
          if (userData.api_key) {
            setAuthSession(userData.api_key, safeStored);
          }

          setAuthLoading(false);
          return;
        }

        // Method 1.5: Check for VS Code extension user data
        const vscodeUser = localStorage.getItem('vscode_user') || localStorage.getItem('candor_vscode_user');
        if (vscodeUser) {
          const userData = JSON.parse(vscodeUser);
          setUser(userData);
          setAuthLoading(false);
          return;
        }

        // Method 1.6: Check for VS Code configuration data
        const vscodeConfig = localStorage.getItem('vscode_config') || localStorage.getItem('candor_config');
        if (vscodeConfig) {
          try {
            const config = JSON.parse(vscodeConfig);
            if (config.currentUserEmail) {
              setUser({
                email: config.currentUserEmail,
                name: config.currentUserName || 'VS Code User',
              });
              setAuthLoading(false);
              return;
            }
          } catch (error) {
            // Ignore malformed config data
          }
        }

        // Method 2: Check for API key from CDP IDE
        const apiKey = getAuthApiKey();
        if (apiKey || hasCookieSession()) {
          try {
            const response = await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_USER_PROFILE || '/api/v1/users/profile'), {
              ...candorWebRequestAuth({ 'Content-Type': 'application/json' }),
            });

            if (response.ok) {
              const userData = await response.json();
              setUser({
                email: userData.email,
                name: userData.name,
                company: userData.company,
                role: userData.role,
                is_active: userData.is_active
              });
              setAuthLoading(false);
              return;
            } else {
              const errorText = await response.text();
              throw new Error(`API request failed: ${response.status} ${errorText}`);
            }
          } catch (apiError) {
            setAuthLoading(false);
            return;
          }
        }

        // No valid authentication found - require proper login
        setAuthLoading(false);

        // Fallback to mock user if all else fails
        if (!user) {
          // Method 3: Check if running inside VS Code extension and try to get real user
          const isVSCode = window.parent !== window ||
            window.location.href.includes('vscode-webview') ||
            document.referrer.includes('vscode');

          if (isVSCode) {
            // Try to get the real logged-in user from VS Code extension
            try {
              // Check if VS Code extension has stored user data
              const vscodeUserData = localStorage.getItem('candor_vscode_user_data');
              if (vscodeUserData) {
                const userData = JSON.parse(vscodeUserData);
                setUser(userData);
                setAuthLoading(false);
                return;
              }

              // Try to get user from VS Code extension via postMessage
              if (window.parent !== window) {
                window.parent.postMessage({
                  type: 'getCurrentUser',
                  source: 'subscription_dashboard'
                }, '*');

                // Wait for response
                const handleUserResponse = (event) => {
                  if (event.data && event.data.type === 'currentUserResponse') {
                    setUser(event.data.user);
                    setAuthLoading(false);
                    window.removeEventListener('message', handleUserResponse);
                  }
                };

                window.addEventListener('message', handleUserResponse);

                // Timeout after 2 seconds
                setTimeout(() => {
                  window.removeEventListener('message', handleUserResponse);
                  if (!user) {
                    setUser(null);
                  }
                }, 2000);
              } else {
                // Try to get user from VS Code configuration or localStorage
                const vscodeConfig = localStorage.getItem('vscode_config') || localStorage.getItem('candor_config');
                if (vscodeConfig) {
                  try {
                    const config = JSON.parse(vscodeConfig);
                    if (config.currentUserEmail) {
                      setUser({
                        email: config.currentUserEmail,
                        name: config.currentUserName || 'VS Code User',
                      });
                      setAuthLoading(false);
                      return;
                    }
                  } catch (error) {
                    // Ignore invalid VS Code config data
                  }
                }

                // Fallback for VS Code extension
                setUser({
                  email: 'vscode-user@candor.com',
                  name: 'VS Code User',
                });
              }
            } catch (error) {
              setUser(null);
            }
          } else {
            // No valid authentication found
            setUser(null);
          }
        }
      } catch (error) {
        setUser(null);
      } finally {
        setAuthLoading(false);
      }
    };

    authenticateUser();
  }, [authContextLoading, authToken, authUser, establishSecureSession, persistSecureSession]);

  // ***** Code Start BH10092026_1400 - BrowserRouter path → section (IDE /upgrade handoff)
  const location = useLocation();
  const navigate = useNavigate();

  const PATH_TO_SECTION = {
    '/upgrade': 'plans',
    '/plans': 'plans',
    '/addons': 'addons',
    '/billing': 'billing',
    '/usage': 'dashboard',
    '/status': 'dashboard',
    '/dashboard': 'dashboard',
    '/subscription-dashboard': 'dashboard',
  };

  const SECTION_TO_PATH = {
    dashboard: '/dashboard',
    plans: '/upgrade',
    addons: '/addons',
    billing: '/billing',
    settings: '/dashboard',
  };

  useEffect(() => {
    const path = String(location.pathname || '').replace(/\/+$/, '') || '/';
    const section = PATH_TO_SECTION[path];
    if (section) {
      setActiveSection(section);
    }
  }, [location.pathname]);

  const goToSection = useCallback((sectionId) => {
    const next = String(sectionId || 'dashboard');
    setActiveSection(next);
    // Settings lives inside the shell without its own URL — keep current path.
    if (next === 'settings') {
      return;
    }
    const targetPath = SECTION_TO_PATH[next] || '/dashboard';
    if (location.pathname !== targetPath) {
      navigate(targetPath, { replace: false });
    }
  }, [location.pathname, navigate]);
  // ***** Code End BH10092026_1400

  // Handle URL hash changes for VS Code extension integration
  useEffect(() => {
    const handleHashChange = () => {
      // Handle both #/addons and #addons formats
      let hash = window.location.hash;
      if (hash.startsWith('#/')) {
        hash = hash.replace('#/', '').split('?')[0]; // Remove query params
      } else if (hash.startsWith('#')) {
        hash = hash.replace('#', '').split('?')[0]; // Remove query params
      }

      if (hash && ['billing', 'usage', 'status', 'addons', 'plans', 'dashboard'].includes(hash)) {
        setActiveSection(hash === 'usage' || hash === 'status' ? 'dashboard' : hash);
      }
    };

    // Check initial hash on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const { isDark, toggleMode } = useThemeMode();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Navigation menu items
  const navGroups = [
    {
      label: 'Workspace',
      items: [
        { id: 'dashboard', label: 'Overview', icon: <DashboardIcon /> },
        { id: 'plans', label: 'Plans & pricing', icon: <CreditCardIcon /> },
        { id: 'addons', label: 'Add-ons', icon: <AddCircleIcon /> },
      ],
    },
    {
      label: 'Account',
      items: [
        { id: 'billing', label: 'Billing history', icon: <TrendingUpIcon /> },
        { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
      ],
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };


  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleSignOut = async () => {
    try {
      setLogoutLoading(true);
      setLogoutProgress(0);

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setLogoutProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      try {
        // Call logout API endpoint
        setLogoutProgress(30);
        await apiService.logout();
        setLogoutProgress(60);

        // Clear stored auth/session data
        setLogoutProgress(70);
        clearAllAuthStorage();
        localStorage.removeItem('vscode_user');
        localStorage.removeItem('candor_vscode_user');
        localStorage.removeItem('candor_csrf_token');
        localStorage.removeItem('candor_session_expires_at');
        localStorage.removeItem(tokenStorageKey);

        setLogoutProgress(90);

        // Small delay to show completion
        await new Promise(resolve => setTimeout(resolve, 200));

        setLogoutProgress(100);
        clearInterval(progressInterval);

        // Redirect to local marketing site after brief delay
        setTimeout(() => {
          window.location.href = '/';
        }, 300);

      } catch (logoutError) {
        clearInterval(progressInterval);
        console.error('Logout error:', logoutError);

        // Even if API call fails, clear local storage and redirect
        try {
          clearAllAuthStorage();
          localStorage.removeItem('vscode_user');
          localStorage.removeItem('candor_vscode_user');
          localStorage.removeItem('candor_csrf_token');
          localStorage.removeItem('candor_session_expires_at');
          localStorage.removeItem(tokenStorageKey);
        } catch (e) {
          // Ignore cleanup warnings during sign out
        }

        // Redirect even on error
        setTimeout(() => {
          window.location.href = '/';
        }, 500);
      }
    } catch (e) {
      console.error('Unexpected error during logout:', e);
      // Ignore cleanup warnings during sign out
      setLogoutLoading(false);
      setLogoutProgress(0);

      // Still try to redirect
      try {
        clearAllAuthStorage();
        localStorage.removeItem('vscode_user');
        localStorage.removeItem('candor_vscode_user');
        localStorage.removeItem('candor_csrf_token');
        localStorage.removeItem('candor_session_expires_at');
        localStorage.removeItem(tokenStorageKey);
      } catch (storageError) {
        // Ignore
      }

      window.location.href = '/';
    }
  };


  const handleExpiredSubscribe = () => {
    goToSection('plans');
    setShowExpiredModal(false);
  };

  useEffect(() => {
    if (authLoading || subscriptionLoading) {
      return;
    }

    const status = subscription?.status?.toLowerCase();
    if (status === 'expired') {
      if (!hasShownExpiredModalRef.current) {
        setShowExpiredModal(true);
        hasShownExpiredModalRef.current = true;
      }
    } else {
      hasShownExpiredModalRef.current = false;
      setShowExpiredModal(false);
    }
  }, [authLoading, subscriptionLoading, subscription]);

  // Removed CDP integration - using simplified dashboard

  // ***** Code Start BH07092026_2145 - Wait for subscription STATUS before shell (billing/usage async)
  // Show loading screen while auth AND subscription status settle.
  // Register/login navigates to /dashboard immediately; without waiting on
  // subscriptionLoading the overview flashes "No plan" for Community Edition.
  // Billing/usage load in background after status (BH10092026_1716) — do not gate shell on them.
  if (authContextLoading || authLoading || subscriptionLoading) {
    return (
      <LoadingScreen
        message="Loading your dashboard..."
        subtitle="Preparing your Community Edition workspace"
      />
    );
  }
  // ***** Code End BH07092026_2145

  // Show error if no user (auth finished but no session found)
  if (!user) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231976d2' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='1.5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            opacity: 0.4,
          }}
        />

        <Container maxWidth="sm">
          <Fade in timeout={800}>
            <Paper
              elevation={24}
              sx={{
                p: 6,
                textAlign: 'center',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              {/* Decorative Elements */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -64,
                  right: -64,
                  width: 128,
                  height: 128,
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                  borderRadius: '50%',
                  opacity: 0.6,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -48,
                  left: -48,
                  width: 96,
                  height: 96,
                  background: 'linear-gradient(45deg, #f3e5f5 0%, #e1bee7 100%)',
                  borderRadius: '50%',
                  opacity: 0.6,
                }}
              />

              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 3,
                    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                    boxShadow: 3,
                  }}
                >
                  <SecurityIcon sx={{ fontSize: 40 }} />
                </Avatar>

                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #1976d2 30%, #9c27b0 90%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Welcome to Candor
                </Typography>

                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ mb: 4, lineHeight: 1.6 }}
                >
                  Please authenticate to access your subscription dashboard and manage your account.
                </Typography>

                <Stack spacing={3}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => window.location.reload()}
                    startIcon={<CheckCircleIcon />}
                    sx={{
                      py: 2,
                      px: 4,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  >
                    Authenticate Now
                  </Button>

                  <Stack
                    direction="row"
                    spacing={4}
                    justifyContent="center"
                    sx={{ mt: 2 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        Secure
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TrendingUpIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        Fast
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SecurityIcon color="secondary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        Modern
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Fade>
        </Container>
      </Box>
    );
  }

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardOverview />;
      case 'plans':
        return <SubscriptionPlans />;
      case 'addons':
        return <AddOnPlans />;
      case 'billing':
        return <BillingHistory currentUser={user} />;
      case 'settings':
        return (
          <div className="candor-settings">
            <div className="candor-panel">
              <div className="candor-panel-header">
                <h3 className="candor-panel-title">Appearance</h3>
              </div>
              <FormControlLabel
                control={<Switch checked={isDark} onChange={toggleMode} color="primary" />}
                label={isDark ? 'Dark mode enabled' : 'Light mode enabled'}
                sx={{ m: 0 }}
              />
              <p className="candor-panel-meta" style={{ marginTop: '0.75rem' }}>
                Theme preference is saved on this device.
              </p>
            </div>
          </div>
        );
      default:
        return <DashboardOverview />;
    }
  };

  const renderActiveSection = () => {
    const meta = SECTION_META[activeSection] || SECTION_META.dashboard;
    const showPageHeader = activeSection !== 'plans';

    return (
      <div className="candor-content-stack">
        {showPageHeader && (
          <PageHeader
            eyebrow={meta.eyebrow}
            title={meta.title}
            description={meta.description}
          />
        )}
        {renderSectionContent()}
      </div>
    );
  };

  return (
    <>
      <Box sx={{ display: 'flex', minHeight: '100vh' }} data-theme={isDark ? 'dark' : 'light'} className="candor-dashboard-shell">
        <AppBar
          position="fixed"
          color="default"
          elevation={0}
          className="candor-app-bar"
          sx={{
            zIndex: (t) => t.zIndex.drawer + 1,
            color: 'text.primary',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: (t) => alpha(t.palette.background.paper, 0.94),
            backdropFilter: 'blur(10px)',
            boxShadow: (t) =>
              t.palette.mode === 'dark'
                ? '0 1px 0 rgba(255,255,255,0.06)'
                : '0 1px 3px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              justifyContent: 'space-between',
              minHeight: { xs: 56, sm: 64 },
              px: { xs: 1.5, sm: 2.5 },
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
              {isMobile && (
                <IconButton
                  aria-label="Open navigation"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{
                    color: 'text.primary',
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <AppHeaderBrand />
              </Box>
              <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
                <AppHeaderBrand compact />
              </Box>
            </Stack>

            <AppToolbarActions
              isDark={isDark}
              onToggleMode={toggleMode}
              onRefresh={async () => {
                try {
                  await refreshData();
                } catch {
                  window.location.reload();
                }
              }}
              user={user}
              userMenuAnchor={userMenuAnchor}
              onUserMenuOpen={handleUserMenuOpen}
            />
          </Toolbar>
        </AppBar>

        {/* Navigation Drawer */}
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          sx={{
            width: 280,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
              bgcolor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
        >
          <Box
            sx={{
              minHeight: { xs: 56, sm: 64 },
              display: 'flex',
              alignItems: 'center',
              px: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <AppHeaderBrand />
          </Box>
          <Box sx={{ overflow: 'auto', px: 1.25, py: 2 }}>
            {navGroups.map((group) => (
              <Box key={group.label} sx={{ mb: 2 }}>
                <Typography
                  variant="overline"
                  sx={{
                    px: 1.5,
                    mb: 0.5,
                    display: 'block',
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                  }}
                >
                  {group.label}
                </Typography>
                <List dense disablePadding>
                  {group.items.map((item) => {
                    const isActive = activeSection === item.id;
                    return (
                      <ListItem key={item.id} disablePadding sx={{ mb: 0.35 }}>
                        <ListItemButton
                          selected={isActive}
                          onClick={() => {
                            goToSection(item.id);
                            if (isMobile) setMobileOpen(false);
                          }}
                          sx={{
                            py: 1.1,
                            px: 1.5,
                            borderRadius: 2,
                            '&.Mui-selected': {
                              bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                              color: 'primary.main',
                              '&:hover': {
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
                              },
                              '& .MuiListItemIcon-root': {
                                color: 'primary.main',
                              },
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 38,
                              color: isActive ? 'primary.main' : 'text.secondary',
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              fontWeight: isActive ? 600 : 500,
                              letterSpacing: '-0.01em',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            ))}
          </Box>
        </Drawer>

        <UserAccountMenu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          user={user}
          subscription={subscription}
          onNavigate={(sectionId) => goToSection(sectionId)}
          onSignOut={handleSignOut}
        />


        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - 280px)` },
            mt: 8,
            bgcolor: 'background.default',
            minHeight: 'calc(100vh - 64px)',
            position: 'relative',
          }}
        >
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, py: 1 }}>
            <Fade in timeout={600}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 'calc(100vh - 120px)',
                }}
              >
                <Box sx={{ p: { xs: 2.5, md: 3.5 }, flex: 1 }}>
                  {renderActiveSection()}
                </Box>

                <footer className="candor-app-footer">
                  <p>© 2026 Candor. Operated by Proden Technologies Incorporated. All rights reserved.</p>
                  <nav className="candor-app-footer-links" aria-label="Legal">
                    <button type="button" onClick={() => setActiveComplianceModal('privacy')}>Privacy Policy</button>
                    <button type="button" onClick={() => setActiveComplianceModal('terms')}>Terms &amp; Conditions</button>
                    <button type="button" onClick={() => setActiveComplianceModal('refund')}>Refund Policy</button>
                  </nav>
                </footer>
              </Paper>
            </Fade>
          </Container>
        </Box>
      </Box>

      <ExpiredSubscriptionModal
        isOpen={showExpiredModal}
        onClose={() => setShowExpiredModal(false)}
        onSubscribe={handleExpiredSubscribe}
      />

      {/* Logout Progress Bar */}
      <Backdrop
        open={logoutLoading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            width: '100%',
            maxWidth: 400,
            p: 4,
          }}
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: 'primary.main',
              mb: 2,
            }}
          />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Signing out...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Please wait while we securely log you out
          </Typography>
          <Box sx={{ width: '100%', mb: 1 }}>
            <LinearProgress
              variant="determinate"
              value={logoutProgress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #1976d2 0%, #9c27b0 100%)',
                },
              }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {logoutProgress}% complete
          </Typography>
        </Box>
      </Backdrop>

      {/* Settings modal removed */}

      <CompliancePolicyModal
        activePolicyId={activeComplianceModal}
        onClose={() => setActiveComplianceModal(null)}
      />
    </>
  );
};

export default Dashboard;
